import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3013/api';

// Parse date string to local date (avoids UTC timezone issues)
const parseLocalDate = (dateString) => {
  if (!dateString) return null;
  const str = String(dateString);
  let dateOnly;
  
  if (str.includes('T')) {
    dateOnly = str.split('T')[0];
  } else if (str.length === 10 && str.includes('-')) {
    dateOnly = str;
  } else {
    const d = new Date(str);
    if (isNaN(d.getTime())) return null;
    dateOnly = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  }
  
  const [year, month, day] = dateOnly.split('-').map(Number);
  return new Date(year, month - 1, day, 0, 0, 0, 0);
};

const hallLabels = {
  main_hall: 'Main Hall',
  back_hall: 'Back Hall',
};

const noticeTypeConfig = {
  general: { color: '#3b82f6', bgColor: 'rgba(59, 130, 246, 0.15)' },
  important: { color: '#f59e0b', bgColor: 'rgba(245, 158, 11, 0.15)' },
  urgent: { color: '#ef4444', bgColor: 'rgba(239, 68, 68, 0.15)' },
  announcement: { color: '#10b981', bgColor: 'rgba(16, 185, 129, 0.15)' },
  reminder: { color: '#8b5cf6', bgColor: 'rgba(139, 92, 246, 0.15)' },
};

const NoticeBoard = () => {
  const [notices, setNotices] = useState([]);
  const [currentEvents, setCurrentEvents] = useState([]);
  const [currentNoticeIndex, setCurrentNoticeIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Fetch data - no auth required for public display
  const fetchData = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/sab/notices/noticeboard`);
      setNotices(response.data.notices || []);
      setCurrentEvents(response.data.currentEvents || []);
    } catch (error) {
      console.error('Error fetching notice board data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    // Refresh data every 30 seconds
    const dataInterval = setInterval(fetchData, 30000);
    return () => clearInterval(dataInterval);
  }, [fetchData]);

  // Update time every second
  useEffect(() => {
    const timeInterval = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);
    return () => clearInterval(timeInterval);
  }, []);

  // Rotate notices
  useEffect(() => {
    if (notices.length <= 1) return;

    const currentNotice = notices[currentNoticeIndex];
    const duration = (currentNotice?.displayDuration || 10) * 1000;

    const rotationInterval = setTimeout(() => {
      setIsTransitioning(true);
      setTimeout(() => {
        setCurrentNoticeIndex((prev) => (prev + 1) % notices.length);
        setIsTransitioning(false);
      }, 500);
    }, duration);

    return () => clearTimeout(rotationInterval);
  }, [currentNoticeIndex, notices]);

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Format event date for display
  const formatEventDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };

  // Check if event is multi-day or recurring daily
  const isMultiDayEvent = (event) => {
    if (event.isRecurringDaily) return true;
    if (!event.startDate || !event.endDate) return false;
    const start = parseLocalDate(event.startDate);
    const end = parseLocalDate(event.endDate);
    if (!start || !end) return false;
    return start.toDateString() !== end.toDateString();
  };

  // Get display time for event (handles recurring daily)
  const getEventDisplayTime = (event) => {
    if (event.isRecurringDaily && event.dailyStartTime && event.dailyEndTime) {
      return { start: event.dailyStartTime, end: event.dailyEndTime };
    }
    return { start: event.startTime, end: event.endTime };
  };

  const hasEvents = currentEvents.length > 0;
  const currentNotice = notices[currentNoticeIndex];

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loadingSpinner} />
        <div style={styles.loadingText}>Loading Notice Board...</div>
      </div>
    );
  }

  // Full screen notices when no events
  if (!hasEvents) {
    return (
      <div style={styles.container}>
        {/* Header */}
        <div style={styles.header}>
          <div style={styles.headerLeft}>
            <img src="/logo.png" alt="GTSA" style={styles.logo} />
            <div>
              <div style={styles.headerTitle}>Golden Triangle Sikh Association</div>
              <div style={styles.headerSub}>ਗੋਲਡਨ ਟ੍ਰਾਈਐਂਗਲ ਸਿੱਖ ਐਸੋਸੀਏਸ਼ਨ</div>
            </div>
          </div>
          <div style={styles.headerRight}>
            <div style={styles.time}>{formatTime(currentTime)}</div>
            <div style={styles.date}>{formatDate(currentTime)}</div>
          </div>
        </div>

        {/* Full Screen Notices */}
        <div style={styles.fullScreenNotices}>
          <div style={styles.sectionTitle}>
            <span style={styles.sectionIcon}>📢</span>
            Notices & Announcements
            <span style={styles.sectionTitlePunjabi}>ਨੋਟਿਸ ਅਤੇ ਘੋਸ਼ਣਾਵਾਂ</span>
          </div>

          {notices.length === 0 ? (
            <div style={styles.noContent}>
              <span style={{ fontSize: 64, marginBottom: 16 }}>🕉️</span>
              <div style={{ fontSize: 24, color: '#F97316' }}>No notices at this time</div>
              <div style={{ color: '#64748b', marginTop: 8 }}>ਇਸ ਸਮੇਂ ਕੋਈ ਨੋਟਿਸ ਨਹੀਂ</div>
            </div>
          ) : (
            <div style={{
              ...styles.fullNoticeCard,
              opacity: isTransitioning ? 0 : 1,
              transform: isTransitioning ? 'translateY(20px)' : 'translateY(0)',
            }}>
              {currentNotice && (
                <>
                  {/* Icon and type badge removed for cleaner look */}
                  {/* Use smaller fonts when table is included */}
                  <div style={currentNotice.hasTable ? styles.fullNoticeTitleWithTable : styles.fullNoticeTitle}>
                    {currentNotice.title}
                  </div>
                  {currentNotice.titlePunjabi && (
                    <div style={currentNotice.hasTable ? styles.fullNoticeTitlePunjabiWithTable : styles.fullNoticeTitlePunjabi}>
                      {currentNotice.titlePunjabi}
                    </div>
                  )}
                  {/* Content is optional when table is included */}
                  {currentNotice.content && (
                    <div style={currentNotice.hasTable ? styles.fullNoticeContentWithTable : styles.fullNoticeContent}>
                      {currentNotice.content}
                    </div>
                  )}
                  {currentNotice.contentPunjabi && (
                    <div style={currentNotice.hasTable ? styles.fullNoticeContentPunjabiWithTable : styles.fullNoticeContentPunjabi}>
                      {currentNotice.contentPunjabi}
                    </div>
                  )}
                  
                  {/* Table Display */}
                  {currentNotice.hasTable && currentNotice.tableData && (
                    <div style={styles.tableContainer}>
                      <table style={styles.noticeTable}>
                        <thead>
                          <tr>
                            {currentNotice.tableData.headers?.map((header, i) => (
                              <th key={i} style={{
                                ...styles.tableHeader,
                                background: currentNotice.color || '#F97316',
                                textAlign: currentNotice.tableAlignment || 'center',
                              }}>
                                {header}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {currentNotice.tableData.rows?.map((row, rowIndex) => (
                            <tr key={rowIndex} style={{
                              background: rowIndex % 2 === 0 ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.02)',
                            }}>
                              {row.map((cell, colIndex) => (
                                <td key={colIndex} style={{
                                  ...styles.tableCell,
                                  textAlign: currentNotice.tableAlignment || 'center',
                                }}>
                                  {cell || '-'}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  
                  {/* Notice indicator dots */}
                  {notices.length > 1 && (
                    <div style={styles.indicatorContainer}>
                      {notices.map((_, idx) => (
                        <div
                          key={idx}
                          style={{
                            ...styles.indicator,
                            background: idx === currentNoticeIndex ? '#F97316' : 'rgba(255,255,255,0.3)',
                            width: idx === currentNoticeIndex ? 24 : 8,
                          }}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          ਵਾਹਿਗੁਰੂ ਜੀ ਕਾ ਖਾਲਸਾ ॥ ਵਾਹਿਗੁਰੂ ਜੀ ਕੀ ਫਤਹਿ ॥
        </div>
      </div>
    );
  }

  // Split screen: Events on left, Notices on right
  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <img src="/logo.png" alt="GTSA" style={styles.logo} />
          <div>
            <div style={styles.headerTitle}>Golden Triangle Sikh Association</div>
            <div style={styles.headerSub}>ਗੋਲਡਨ ਟ੍ਰਾਈਐਂਗਲ ਸਿੱਖ ਐਸੋਸੀਏਸ਼ਨ</div>
          </div>
        </div>
        <div style={styles.headerRight}>
          <div style={styles.time}>{formatTime(currentTime)}</div>
          <div style={styles.date}>{formatDate(currentTime)}</div>
        </div>
      </div>

      {/* Main Content - Split View */}
      <div style={styles.mainContent}>
        {/* Left Side - Current Events */}
        <div style={styles.leftPanel}>
          <div style={styles.sectionTitle}>
            <span style={styles.sectionIcon}>🙏</span>
            Happening Now
            <span style={styles.sectionTitlePunjabi}>ਹੁਣ ਚੱਲ ਰਿਹਾ ਹੈ</span>
          </div>

          <div style={styles.eventsContainer}>
            {currentEvents.map((event, index) => (
              <div
                key={event._id}
                style={{
                  ...styles.eventCard,
                  borderLeftColor: event.programTypeColor || '#F97316',
                  animationDelay: `${index * 0.1}s`,
                }}
              >
                {/* Hall Badge */}
                <div style={{
                  ...styles.hallBadge,
                  background: event.hall === 'main_hall' ? '#F97316' : '#3b82f6',
                }}>
                  {hallLabels[event.hall] || event.hall}
                </div>

                {/* Event Type */}
                <div style={styles.eventType}>
                  {event.programTypeName || 'Program'}
                </div>

                {/* Booker Name */}
                <div style={styles.eventBooker}>
                  <span style={styles.bookerIcon}>👤</span>
                  {event.bookingName}
                </div>

                {/* Occasion if any */}
                {event.occasion && (
                  <div style={styles.eventOccasion}>
                    {getOccasionDisplay(event.occasion, event.occasionCustom)}
                  </div>
                )}

                {/* Date & Time */}
                <div style={styles.eventTimeContainer}>
                  {isMultiDayEvent(event) ? (
                    <>
                      <div style={styles.eventDateRange}>
                        <span style={styles.dateIcon}>📅</span>
                        {formatEventDate(event.startDate)} - {formatEventDate(event.endDate)}
                        {event.isRecurringDaily && event.totalDays && (
                          <span style={styles.totalDaysBadge}> ({event.totalDays} days)</span>
                        )}
                      </div>
                      <div style={styles.eventTime}>
                        <span style={styles.timeIcon}>🕐</span>
                        {getEventDisplayTime(event).start} - {getEventDisplayTime(event).end}
                        {event.isRecurringDaily && <span style={styles.dailyLabel}> Daily</span>}
                      </div>
                    </>
                  ) : (
                    <div style={styles.eventTime}>
                      <span style={styles.timeIcon}>🕐</span>
                      {event.startTime} - {event.endTime}
                    </div>
                  )}
                </div>

                {/* Add-ons */}
                <div style={styles.eventAddons}>
                  {event.includeKirtan && (
                    <span style={styles.addonBadge}>🎵 Kirtan</span>
                  )}
                  {event.includeLangar && (
                    <span style={styles.addonBadge}>🍽️ Langar</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right Side - Notices */}
        <div style={styles.rightPanel}>
          <div style={styles.sectionTitle}>
            <span style={styles.sectionIcon}>📢</span>
            Notices
            <span style={styles.sectionTitlePunjabi}>ਨੋਟਿਸ</span>
          </div>

          {notices.length === 0 ? (
            <div style={styles.noNotices}>
              <span style={{ fontSize: 48 }}>✅</span>
              <div>No notices at this time</div>
            </div>
          ) : (
            <div style={{
              ...styles.noticeCard,
              opacity: isTransitioning ? 0 : 1,
              transform: isTransitioning ? 'translateX(20px)' : 'translateX(0)',
            }}>
              {currentNotice && (
                <>
                  {/* Hide icon and type badge in split view for cleaner look */}
                  {/* Use smaller fonts when table is included */}
                  <div style={currentNotice.hasTable ? styles.noticeTitleSplitWithTable : styles.noticeTitleSplit}>
                    {currentNotice.title}
                  </div>
                  {currentNotice.titlePunjabi && (
                    <div style={currentNotice.hasTable ? styles.noticeTitlePunjabiSplitWithTable : styles.noticeTitlePunjabiSplit}>
                      {currentNotice.titlePunjabi}
                    </div>
                  )}
                  {/* Content is optional when table is included */}
                  {currentNotice.content && (
                    <div style={currentNotice.hasTable ? styles.noticeContentSplitWithTable : styles.noticeContentSplit}>
                      {currentNotice.content}
                    </div>
                  )}
                  {currentNotice.contentPunjabi && (
                    <div style={currentNotice.hasTable ? styles.noticeContentPunjabiSplitWithTable : styles.noticeContentPunjabiSplit}>
                      {currentNotice.contentPunjabi}
                    </div>
                  )}
                  
                  {/* Table Display */}
                  {currentNotice.hasTable && currentNotice.tableData && (
                    <div style={styles.tableContainerSmall}>
                      <table style={styles.noticeTableSmall}>
                        <thead>
                          <tr>
                            {currentNotice.tableData.headers?.map((header, i) => (
                              <th key={i} style={{
                                ...styles.tableHeaderSmall,
                                background: currentNotice.color || '#F97316',
                                textAlign: currentNotice.tableAlignment || 'center',
                              }}>
                                {header}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {currentNotice.tableData.rows?.map((row, rowIndex) => (
                            <tr key={rowIndex} style={{
                              background: rowIndex % 2 === 0 ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.02)',
                            }}>
                              {row.map((cell, colIndex) => (
                                <td key={colIndex} style={{
                                  ...styles.tableCellSmall,
                                  textAlign: currentNotice.tableAlignment || 'center',
                                }}>
                                  {cell || '-'}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  {/* Indicator */}
                  {notices.length > 1 && (
                    <div style={styles.indicatorContainer}>
                      {notices.map((_, idx) => (
                        <div
                          key={idx}
                          style={{
                            ...styles.indicator,
                            background: idx === currentNoticeIndex ? '#F97316' : 'rgba(255,255,255,0.3)',
                            width: idx === currentNoticeIndex ? 20 : 6,
                          }}
                        />
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div style={styles.footer}>
        ਵਾਹਿਗੁਰੂ ਜੀ ਕਾ ਖਾਲਸਾ ॥ ਵਾਹਿਗੁਰੂ ਜੀ ਕੀ ਫਤਹਿ ॥
      </div>
    </div>
  );
};

// Helper function for occasion display
const getOccasionDisplay = (occasion, custom) => {
  const occasionMap = {
    birthday: '🎂 Birthday',
    house_warming: '🏠 House Warming',
    wedding_anniversary: '💍 Anniversary',
    baby_shower: '👶 Baby Shower',
    retirement: '🎊 Retirement',
    graduation: '🎓 Graduation',
    memorial: '🕯️ Memorial',
    thanksgiving: '🙏 Thanksgiving',
    other: custom ? `📝 ${custom}` : '📝 Special Occasion',
  };
  return occasionMap[occasion] || occasion;
};

const styles = {
  container: {
    width: '100vw',
    height: '100vh',
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
    display: 'flex',
    flexDirection: 'column',
    fontFamily: "'Segoe UI', system-ui, sans-serif",
    overflow: 'hidden',
    padding: 16,
    boxSizing: 'border-box',
  },
  loadingContainer: {
    width: '100vw',
    height: '100vh',
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 20,
  },
  loadingSpinner: {
    width: 48,
    height: 48,
    border: '4px solid rgba(249, 115, 22, 0.3)',
    borderTopColor: '#F97316',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    color: '#F97316',
    fontSize: 18,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 24px',
    background: 'rgba(0,0,0,0.3)',
    borderRadius: 12,
    marginBottom: 12,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
  },
  logo: {
    width: 56,
    height: 56,
  },
  headerTitle: {
    color: '#F97316',
    fontSize: 24,
    fontWeight: 700,
  },
  headerSub: {
    color: '#94a3b8',
    fontSize: 14,
  },
  headerRight: {
    textAlign: 'right',
  },
  time: {
    color: '#fff',
    fontSize: 36,
    fontWeight: 700,
    lineHeight: 1,
  },
  date: {
    color: '#94a3b8',
    fontSize: 14,
    marginTop: 4,
  },
  mainContent: {
    flex: 1,
    display: 'flex',
    gap: 12,
    overflow: 'hidden',
  },
  leftPanel: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    background: 'rgba(255,255,255,0.03)',
    borderRadius: 12,
    padding: 16,
    overflow: 'hidden',
  },
  rightPanel: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    background: 'rgba(255,255,255,0.03)',
    borderRadius: 12,
    padding: 16,
    overflow: 'hidden',
  },
  sectionTitle: {
    color: '#fff',
    fontSize: 22,
    fontWeight: 700,
    marginBottom: 16,
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  sectionIcon: {
    fontSize: 28,
  },
  sectionTitlePunjabi: {
    color: '#F97316',
    fontSize: 14,
    fontWeight: 400,
    marginLeft: 'auto',
  },
  eventsContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    overflow: 'auto',
  },
  eventCard: {
    background: 'rgba(255,255,255,0.05)',
    borderRadius: 12,
    padding: 20,
    borderLeft: '6px solid #F97316',
    animation: 'slideIn 0.5s ease-out',
  },
  hallBadge: {
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: 20,
    color: '#fff',
    fontSize: 12,
    fontWeight: 600,
    marginBottom: 12,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  eventType: {
    color: '#fff',
    fontSize: 28,
    fontWeight: 700,
    marginBottom: 8,
    lineHeight: 1.2,
  },
  eventBooker: {
    color: '#F97316',
    fontSize: 22,
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
  },
  bookerIcon: {
    fontSize: 20,
  },
  eventOccasion: {
    color: '#94a3b8',
    fontSize: 16,
    marginBottom: 8,
    background: 'rgba(249, 115, 22, 0.1)',
    padding: '4px 12px',
    borderRadius: 6,
    display: 'inline-block',
  },
  eventTimeContainer: {
    marginBottom: 8,
  },
  eventDateRange: {
    color: '#F97316',
    fontSize: 16,
    fontWeight: 600,
    marginBottom: 4,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  eventTime: {
    color: '#64748b',
    fontSize: 16,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  totalDaysBadge: {
    color: '#94a3b8',
    fontSize: 14,
    fontWeight: 500,
  },
  dailyLabel: {
    color: '#10b981',
    fontSize: 12,
    fontWeight: 600,
    marginLeft: 4,
    background: 'rgba(16,185,129,0.15)',
    padding: '2px 6px',
    borderRadius: 4,
  },
  dateIcon: {
    fontSize: 14,
  },
  timeIcon: {
    fontSize: 14,
  },
  eventAddons: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
  },
  addonBadge: {
    background: 'rgba(255,255,255,0.1)',
    color: '#fff',
    padding: '4px 10px',
    borderRadius: 6,
    fontSize: 13,
  },
  noticeCard: {
    flex: 1,
    background: 'rgba(255,255,255,0.05)',
    borderRadius: 12,
    padding: 30,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    transition: 'all 0.5s ease-out',
    overflow: 'hidden',
  },
  noticeHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 16,
  },
  noticeIcon: {
    fontSize: 64,
  },
  noticeTypeBadgeSmall: {
    padding: '8px 16px',
    borderRadius: 8,
    fontSize: 15,
    fontWeight: 700,
    letterSpacing: '0.5px',
  },
  noticeTitle: {
    color: '#fff',
    fontSize: 38,
    fontWeight: 700,
    marginBottom: 12,
    lineHeight: 1.3,
  },
  noticeTitlePunjabi: {
    color: '#F97316',
    fontSize: 30,
    marginBottom: 18,
  },
  noticeContent: {
    color: 'rgba(255,255,255,0.9)',
    fontSize: 28,
    lineHeight: 1.6,
    flex: 1,
  },
  noticeContentPunjabi: {
    color: 'rgba(255,255,255,0.6)',
    fontSize: 24,
    marginTop: 14,
  },
  // Split view styles (half screen - medium fonts, centered)
  noticeTitleSplit: {
    color: '#fff',
    fontSize: 40,
    fontWeight: 700,
    marginBottom: 12,
    lineHeight: 1.3,
    textAlign: 'center',
  },
  noticeTitlePunjabiSplit: {
    color: '#F97316',
    fontSize: 36,
    marginBottom: 20,
    textAlign: 'center',
  },
  noticeContentSplit: {
    color: 'rgba(255,255,255,0.9)',
    fontSize: 36,
    lineHeight: 1.6,
    textAlign: 'center',
  },
  noticeContentPunjabiSplit: {
    color: 'rgba(255,255,255,0.6)',
    fontSize: 36,
    marginTop: 16,
    lineHeight: 1.5,
    textAlign: 'center',
  },
  // Split view with table (smaller fonts)
  noticeTitleSplitWithTable: {
    color: '#fff',
    fontSize: 26,
    fontWeight: 700,
    marginBottom: 8,
    lineHeight: 1.3,
    textAlign: 'center',
  },
  noticeTitlePunjabiSplitWithTable: {
    color: '#F97316',
    fontSize: 20,
    marginBottom: 12,
    textAlign: 'center',
  },
  noticeContentSplitWithTable: {
    color: 'rgba(255,255,255,0.9)',
    fontSize: 16,
    lineHeight: 1.5,
    textAlign: 'center',
    marginBottom: 12,
  },
  noticeContentPunjabiSplitWithTable: {
    color: 'rgba(255,255,255,0.6)',
    fontSize: 14,
    marginTop: 6,
    lineHeight: 1.4,
    textAlign: 'center',
    marginBottom: 12,
  },
  noNotices: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#64748b',
    fontSize: 18,
    gap: 12,
  },
  indicatorContainer: {
    display: 'flex',
    gap: 6,
    justifyContent: 'center',
    marginTop: 'auto',
    paddingTop: 16,
  },
  indicator: {
    height: 6,
    borderRadius: 3,
    transition: 'all 0.3s ease',
  },
  // Full screen notice styles
  fullScreenNotices: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    background: 'rgba(255,255,255,0.03)',
    borderRadius: 12,
    padding: 24,
    overflow: 'hidden',
  },
  fullNoticeCard: {
    flex: 1,
    background: 'rgba(255,255,255,0.05)',
    borderRadius: 16,
    padding: 40,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    transition: 'all 0.5s ease-out',
  },
  fullNoticeHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    marginBottom: 24,
  },
  fullNoticeIcon: {
    fontSize: 96,
  },
  noticeTypeBadge: {
    padding: '10px 24px',
    borderRadius: 10,
    fontSize: 18,
    fontWeight: 700,
    letterSpacing: '1px',
  },
  fullNoticeTitle: {
    color: '#fff',
    fontSize: 60,
    fontWeight: 700,
    marginBottom: 16,
    lineHeight: 1.2,
  },
  fullNoticeTitlePunjabi: {
    color: '#F97316',
    fontSize: 46,
    marginBottom: 36,
  },
  fullNoticeContent: {
    color: 'rgba(255,255,255,0.9)',
    fontSize: 38,
    lineHeight: 1.7,
    maxWidth: 1100,
  },
  fullNoticeContentPunjabi: {
    color: 'rgba(255,255,255,0.6)',
    fontSize: 32,
    marginTop: 28,
    lineHeight: 1.6,
    maxWidth: 1000,
  },
  // Full screen with table (smaller fonts)
  fullNoticeTitleWithTable: {
    color: '#fff',
    fontSize: 36,
    fontWeight: 700,
    marginBottom: 10,
    lineHeight: 1.2,
  },
  fullNoticeTitlePunjabiWithTable: {
    color: '#F97316',
    fontSize: 28,
    marginBottom: 16,
  },
  fullNoticeContentWithTable: {
    color: 'rgba(255,255,255,0.9)',
    fontSize: 22,
    lineHeight: 1.5,
    marginBottom: 16,
  },
  fullNoticeContentPunjabiWithTable: {
    color: 'rgba(255,255,255,0.6)',
    fontSize: 18,
    marginTop: 8,
    lineHeight: 1.4,
    marginBottom: 16,
  },
  noContent: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  footer: {
    marginTop: 12,
    padding: '10px 16px',
    background: 'rgba(0,0,0,0.3)',
    borderRadius: 10,
    textAlign: 'center',
    color: '#F97316',
    fontSize: 16,
  },
  // Table styles for full screen
  tableContainer: {
    marginTop: 32,
    overflowX: 'auto',
    width: '100%',
  },
  noticeTable: {
    width: '100%',
    borderCollapse: 'collapse',
    borderRadius: 12,
    overflow: 'hidden',
  },
  tableHeader: {
    padding: '14px 20px',
    textAlign: 'left',
    color: '#fff',
    fontWeight: 700,
    fontSize: 18,
    borderBottom: '2px solid rgba(255,255,255,0.2)',
  },
  tableCell: {
    padding: '12px 20px',
    color: 'rgba(255,255,255,0.9)',
    fontSize: 16,
    borderBottom: '1px solid rgba(255,255,255,0.1)',
  },
  // Table styles for split screen (smaller)
  tableContainerSmall: {
    marginTop: 16,
    overflowX: 'auto',
    width: '100%',
  },
  noticeTableSmall: {
    width: '100%',
    borderCollapse: 'collapse',
    borderRadius: 8,
    overflow: 'hidden',
  },
  tableHeaderSmall: {
    padding: '10px 14px',
    textAlign: 'left',
    color: '#fff',
    fontWeight: 600,
    fontSize: 14,
    borderBottom: '2px solid rgba(255,255,255,0.2)',
  },
  tableCellSmall: {
    padding: '8px 14px',
    color: 'rgba(255,255,255,0.9)',
    fontSize: 13,
    borderBottom: '1px solid rgba(255,255,255,0.1)',
  },
};

// Add keyframe animations via style tag
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateX(-20px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
`;
document.head.appendChild(styleSheet);

export default NoticeBoard;
